<template lang="pug">
.send-message-history-tab
  ScheduledMessageListTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @onConfirm="onConfirm"
    @onChangeTime="onChangeTime"
    @onDelete="onDelete"
  )
  .table-options.d-flex.flex-row.align-items-center
    .d-flex.flex-row.flex-fill.justify-content-end.align-items-center
      // 検索
      .search-filter.d-flex.mr-1.align-items-center.mr-2
        b-input-group.search-filter-input-group.mr-1(size="md")
          b-input-group-prepend
            b-input-group-text.search-filter-icon
              .search-icon
          b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      // ページあたりの行数
      span.table-option-text 1ページの行数:
      .per-page
        b-form-select(
          id="scheduled-per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="md"
          @change="selectPerPage"
        )
      // ページネーション
      b-pagination.m-0(
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        :disabled="isLoading"
        @change="selectPage"
        hide-goto-end-buttons
      )

  // 削除確認モーダル
  CheckDeleteModal(v-model="showConfirmDeleteModal" title="送信予約をキャンセルします" @ok="deleteScheduledMessage(deleteTargetItem)")
    template(#message)
      div 予約送信をキャンセルします。よろしいですか？

  // 送信プレビューモーダル
  SendMessageModal(
    ref="messagePreviewModal"
    :files="previewFiles"
    :isSchedulingMessage="false"
    @preview="onPreview"
  )

  //- ファイルプレビューモーダル
  SendMessageFilePreviewModal(ref="filePreviewModal")

  OkModal(v-model="showErrorModal" :title="errorModalTitle" @ok="onErrorModalClose")

  UpdateScheduledTimeModal(ref="updateScheduledTimeModal" @submit="updateScheduledTime")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import ScheduledMessages, { ScheduledMessageGetModel, ScheduledMessageGetsQuery } from "@/commons/services/ScheduledMessages";
import S3UrlService from "@/commons/services/S3Url";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ScheduledMessageListTable, { FilterSetting, ScheduledMessageTableField, ScheduledMessageTableItem } from "@/pc/containers/sendMessages/ScheduledMessageListTable.vue";
import SendMessageModal from "@/pc/components/modals/SendMessageModal.vue";
import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";
import CheckDeleteModal from "@/pc/components/modals/CheckDeleteModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import UpdateScheduledTimeModal from "@/pc/components/modals/UpdateScheduledTimeModal.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * 送信予約メッセージ一覧コンポーネント
 */
@Component({
  components: {
    PageLoading,
    ScheduledMessageListTable,
    SendMessageModal,
    SendMessageFilePreviewModal,
    CheckDeleteModal,
    OkModal,
    UpdateScheduledTimeModal
  }
})
export default class ScheduledMessageList extends Vue {
  /** 待機者メッセージ一覧か否か */
  @Prop({ type: Boolean, default: false })
  isWaiter!: boolean;

  /** 予約送信メッセージサービス */
  @Inject("ScheduledMessagesService") scheduledMessagesService!: ScheduledMessages;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 予約メッセージ列名 */
  fields: Array<ScheduledMessageTableField> = [];
  /** 予約メッセージの情報 */
  items: Array<ScheduledMessageTableItem> = [];
  /** 編集対象の予約メッセージ */
  updateTargetItem: ScheduledMessageTableItem | null = null;
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** エラーモーダル表示可否 */
  showErrorModal: boolean = false;
  /** エラーモーダルタイトル */
  errorModalTitle: string = "";

  /** フィルター条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendMessage.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendMessage.pageOptions;

  /** 削除確認モーダル表示可否 */
  showConfirmDeleteModal: boolean = false;
  /** 削除対象アイテム */
  deleteTargetItem: ScheduledMessageTableItem | null = null;

  /** プレビューするファイルオブジェクト */
  previewFiles: Array<File> = [];

  /**
   * ### 1ページあたりの表示行数の変更イベント
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `scheduled-message-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      const perPage: number = Number(cookieMap["scheduled-message-list-per-page"]);
      return isNaN(perPage) ? null : perPage;
    }
    return null;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    const fields: Array<ScheduledMessageTableField> = this.createMessageFields();
    this.fields = fields;

    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.sendMessage.perPage);

    await this.initialize();
    return;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;
    this.items = [];
    this.currentPage = 1;

    await this.createView();
    this.isLoading = false;
    return;
  }

  /**
   * ### メッセージ列定義作成
   */
  public createMessageFields(): Array<ScheduledMessageTableField> {
    let fields: Array<ScheduledMessageTableField> = [];

    fields.push({ key: "scheduledTime", label: "送信予定日時", sortable: false, sortDirection: "desc", class: "datetime text-center" });
    fields.push({ key: "type", label: "送信種別", sortable: false, sortDirection: "desc", class: "type text-center" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "writtenBy", label: "送信者", sortable: false, sortDirection: "desc", class: "written-by" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  public async createView(): Promise<void> {
    try {
      const queryParams: ScheduledMessageGetsQuery = {
        status: "scheduled",
        target: this.isWaiter ? "waiter" : "family"
      };

      // 予約メッセージ総数を取得
      const count: number = await this.scheduledMessagesService.getCount(userStore, queryParams);
      this.rows = count;

      // 予約メッセージ一覧を取得
      const startIdx = (this.currentPage - 1) * this.perPage;
      const endIdx = startIdx + this.perPage;

      queryParams.startIndex = startIdx;
      queryParams.endIndex = endIdx;

      const records: Array<ScheduledMessageGetModel> = await this.scheduledMessagesService.gets(userStore, queryParams);

      const items: Array<ScheduledMessageTableItem> = [];
      for (const record of records) {
        let payload: any = {};
        try {
          payload = typeof record.payload === "string" ? JSON.parse(record.payload) : record.payload;
        } catch (e) {
          console.error("Failed to parse payload for scheduled message", record.scheduledMessageId, e);
        }

        const body = payload.body || {};
        const receiverDetails = record.receiverDetails || [];

        // 宛先文字列を作成する
        const receivers: string[] = [];
        if (receiverDetails.length > 0) {
          for (const receiver of receiverDetails) {
            const receiverName: string = receiver.room && receiver.residentName ? `${receiver.name}（${receiver.room}・${receiver.residentName}）` : receiver.name;
            receivers.push(receiverName);
          }
        } else {
          receivers.push(body.targetName || "");
        }

        items.push({
          facilityId: record.facilityId,
          sortKey: record.scheduledMessageId,
          scheduledTime: record.scheduledTime,
          type: record.type,
          status: record.status,
          receivers: receivers.join("、"),
          functionContents: {
            title: body.title || "",
            content: body.message || "",
            addOptionText: body.addOptionText || false,
            addReadButton: body.addReadButton || false,
            addReplyButton: body.addReplyButton || false,
            replyLimit: body.replyLimit !== undefined ? Number(body.replyLimit) : 0
          },
          writerId: body.writerId || "",
          writtenBy: body.writtenBy || "",
          fileUrls: body.fileUrls || [],
          fileNames: body.fileNames || [],
          receiverDetails: receiverDetails,
          payload: payload,
          updated: record.updated
        });
      }

      this.items = items;
    } catch (error) {
      errorHandle(error, this);
    }
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    await this.createView();
    return;
  }

  /**
   * ### ページネーション of ページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    await this.createView();
    return;
  }

  /**
   * ### メッセージの詳細をモーダルで表示する
   */
  public async onConfirm(item: ScheduledMessageTableItem, event?: Event): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 添付ファイルがあれば取得する
    if (item.files) {
      this.previewFiles = item.files;
    } else {
      const files: Array<File> = [];
      if (item.fileUrls && item.fileUrls.length > 0) {
        for (let i = 0; i < item.fileUrls.length; i++) {
          try {
            const file = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, item.fileUrls[i], item.fileNames[i]);
            files.push(file);
          } catch (e) {
            console.error("Failed to download file from S3", item.fileUrls[i], e);
          }
        }
      }
      item.files = files;
      this.previewFiles = files;
    }

    // プレビュー表示用モーダルを呼び出す
    const receivers = item.receiverDetails && item.receiverDetails.length > 0 ? item.receiverDetails : [{ sortKey: "dummy", name: item.receivers }];
    const modal: any = this.$refs.messagePreviewModal;
    if (modal) {
      modal.show(receivers, item.functionContents, false, false);
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### 添付ファイルプレビュー表示
   */
  public onPreview(): void {
    const modal: any = this.$refs.filePreviewModal;
    if (modal) {
      modal.show("", false, this.previewFiles);
    }
  }

  /**
   * ### 日時変更ボタン押下イベント
   */
  public async onChangeTime(item: ScheduledMessageTableItem): Promise<void> {
    this.updateTargetItem = item;
    const modal: any = this.$refs.updateScheduledTimeModal;
    if (modal) {
      modal.show(item.scheduledTime);
    }
    return;
  }

  /**
   * ### 送信予定日時の変更実行
   */
  public async updateScheduledTime(scheduledTime: number): Promise<void> {
    if (!this.updateTargetItem) return;

    this.isBusy = true;
    this.loadingMessage = "更新中...";
    const modal: any = this.$refs.updateScheduledTimeModal;

    try {
      const requestBody = {
        scheduledTime: scheduledTime,
        payload: this.updateTargetItem.payload,
        updated: this.updateTargetItem.updated
      };
      await this.scheduledMessagesService.patch(userStore, this.updateTargetItem.sortKey, requestBody);
      // 送信日時変更に成功したため、日時変更モーダルを閉じる
      if (modal) {
        modal.hide();
      }
      await this.initialize();
    } catch (error) {
      const systemError = error as SystemError;
      const responseMessage = (systemError.message || "") + (systemError.originalResponse || "");

      const isTimeValidationError =
        systemError.statusCode === 400 &&
        (responseMessage.includes("scheduledTime must be at least 10 minutes in the future") ||
          responseMessage.includes("Cannot modify or cancel because it is within 10 minutes of the scheduled time.") ||
          responseMessage.includes("Scheduled message has already been sent"));

      if (isTimeValidationError) {
        let alertMessage = "";
        if (responseMessage.includes("Cannot modify or cancel because it is within 10 minutes of the scheduled time.")) {
          alertMessage = "送信予定時間の10分前を過ぎているため、変更できません。";
        } else if (responseMessage.includes("Scheduled message has already been sent")) {
          alertMessage = "送信予定時刻を過ぎて送信済みのため、変更できません。";
        } else if (responseMessage.includes("scheduledTime must be at least 10 minutes in the future")) {
          alertMessage = "送信予定時刻は現在から10分以上先を指定してください。";
        }

        if (alertMessage) {
          if (modal) {
            modal.setError(alertMessage);
          }
          // バリデーションエラー時は、モーダルを開いたままにしてエラーを表示・再入力させるため、処理を抜ける（モーダルを閉じない）
          return;
        }
      }

      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode === 409) {
        if (modal) {
          modal.setError("他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。");
        }
        await this.initialize();
        // コンフリクトエラー時は、モーダルを開いたままにして再読み込み後の情報での再入力を促すため、処理を抜ける（モーダルを閉じない）
        return;
      }

      // バリデーションエラー以外の予期せぬエラー発生時は、エラー画面遷移前に最前面にモーダルが残るのを防ぐため非表示にする
      if (modal) {
        modal.hide();
      }
      throw error;
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: ScheduledMessageTableItem): void {
    this.showConfirmDeleteModal = true;
    this.deleteTargetItem = item;
    return;
  }

  /**
   * ### 予約メッセージの削除実行
   */
  public async deleteScheduledMessage(item: ScheduledMessageTableItem | null): Promise<void> {
    if (!item) return;

    this.isBusy = true;
    this.loadingMessage = "削除中...";
    // 削除通信の開始に先立ち、削除確認モーダルをあらかじめ非表示にする
    this.showConfirmDeleteModal = false;

    try {
      await this.scheduledMessagesService.delete(userStore, item.sortKey);

      // フロント側のキャッシュから削除対象を除外し、不要なAPIリクエストを削減する
      this.items = this.items.filter((x) => x.sortKey !== item.sortKey);
      this.rows = Math.max(0, this.rows - 1);

      // 削除によって現在のページにデータが無くなった場合は、最新のデータを読み込み直す
      if (this.items.length === 0) {
        if (this.currentPage > 1) {
          this.currentPage--;
        }
        await this.initialize();
      }
    } catch (error) {
      const systemError = error as SystemError;
      const responseMessage = (systemError.message || "") + (systemError.originalResponse || "");

      const isCancelValidationError =
        systemError.statusCode === 400 &&
        (responseMessage.includes("Cannot modify or cancel because it is within 10 minutes of the scheduled time.") ||
          responseMessage.includes("Scheduled message has already been sent") ||
          responseMessage.includes("Scheduled message cannot be canceled"));

      if (isCancelValidationError) {
        let alertMessage = "";
        if (responseMessage.includes("Cannot modify or cancel because it is within 10 minutes of the scheduled time.")) {
          alertMessage = "送信予定時間の10分前を過ぎているため、取り消しできません。";
        } else if (responseMessage.includes("Scheduled message has already been sent")) {
          alertMessage = "送信予定時刻を過ぎて送信済みのため、取り消しできません。";
        } else if (responseMessage.includes("Scheduled message cannot be canceled")) {
          alertMessage = "この送信予約は既にキャンセルされているため、取り消しできません。";
        }

        if (alertMessage) {
          this.errorModalTitle = alertMessage;
          this.showErrorModal = true;
          return;
        }
      }

      throw error;
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### エラーモーダルが閉じられたときのイベント
   */
  public async onErrorModalClose(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### 画面の作り直し (親コンポーネントから呼び出される)
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### 画面の作り直し (親コンポーネントから呼び出される - ローディングなし)
   */
  public async refreshByNoLoading(): Promise<void> {
    await this.createView();
    return;
  }
}
</script>

<style lang="scss" scoped>
.send-message-history-tab {
  .per-page {
    width: 80px;
  }
}
</style>
